import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft,
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronRight,
  Save,
  Eye,
  Upload,
  Video,
  FileText,
  Brain,
  Package,
  Link as LinkIcon,
  HelpCircle,
  CheckCircle,
  AlertCircle,
  Loader2,
  Settings,
  Play,
  Image,
  Layers,
  BookOpen,
  Sparkles,
  FolderOpen,
  Clock,
  Beaker,
  FlaskConical,
  LayoutList,
  Globe,
  Building2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import CourseImageManager from '../components/admin/CourseImageManager';
import ContentPreviewModal, { StatusBadge } from '../components/admin/ContentPreviewModal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { API_BASE } from '../config/api';
import { getThumbnailUrl } from '../lib/utils';

const API = `${API_BASE}/api`;

// Content type configurations for Topics
const CONTENT_TYPES = {
  ai_generated: {
    label: 'AI Generated',
    icon: Brain,
    color: 'text-[#1EBFB3]',
    bgColor: 'bg-[#1EBFB3]/20',
    borderColor: 'border-[#1EBFB3]/30',
    description: 'Generate content using Claude AI'
  },
  learning_lab: {
    label: 'Learning Lab',
    icon: Beaker,
    color: 'text-teal-400',
    bgColor: 'bg-teal-500/20',
    borderColor: 'border-teal-500/30',
    description: 'Add a simulation-based Learning Lab'
  },
  existing_course: {
    label: 'Existing Course',
    icon: BookOpen,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/20',
    borderColor: 'border-cyan-500/30',
    description: 'Link to an existing course'
  },
  quiz: {
    label: 'Quiz',
    icon: HelpCircle,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/20',
    borderColor: 'border-yellow-500/30',
    description: 'Add assessment quiz'
  },
  video: {
    label: 'Video',
    icon: Video,
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
    borderColor: 'border-red-500/30',
    description: 'Upload or embed video content'
  },
  embed: {
    label: 'Embed/URL',
    icon: LinkIcon,
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
    borderColor: 'border-green-500/30',
    description: 'Embed external content (YouTube, Vimeo, etc.)'
  },
  document: {
    label: 'Document',
    icon: FileText,
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/20',
    borderColor: 'border-orange-500/30',
    description: 'Upload PDF or document'
  }
};

const CATEGORIES = [
  "Advanced Certificate",
  "Certificate",
  "Certifications",
  "Micro-learning Unit",
  "Short Course",
  "Specialist Courses",
  "Compliance & Regulatory",
  "Leadership & Management",
  "Project Management",
  "Ethics & Governance"
];

const TARGET_ROLES = [
  "FAIS Representative / Advisor",
  "Key Individual",
  "Compliance Officer",
  "Operations",
  "Legal / Risk",
  "Audit",
  "IT Operations",
  "Governance",
  "Human Resources",
  "Client Fulfilment and Administration",
  "Other Support Function"
];

const SPECIALISATIONS = [
  "Anti-Corruption",
  "Anti-Financial Crime",
  "Anti-Money Laundering",
  "Artificial Intelligence",
  "Compliance",
  "Conduct",
  "Cybersecurity",
  "Data Privacy",
  "Fraud",
  "GRC",
  "IT Governance",
  "Operations",
  "Risk Management",
  "Social Impact Compliance"
];

export default function CourseBuilder() {
  const { courseId } = useParams();
  const { token, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const isEditMode = !!courseId;
  
  // Course state
  const [course, setCourse] = useState({
    title: '',
    description: '',
    category: '',
    specialisation: '',
    level: 'beginner',
    price: 0,
    currency: 'ZAR',
    duration_hours: 1,
    cpd_points: 0,
    instructor_name: '',
    thumbnail_url: '',
    visible_on_master: true,
    publish_to_tenants: [],
    publish_to_all_orgs: false,
    is_published: false,
    topics: []
  });
  
  const [availableOrgs, setAvailableOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedTopics, setExpandedTopics] = useState({});
  const [expandedModules, setExpandedModules] = useState({});
  const [activeContentDialog, setActiveContentDialog] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [mediaLibraryOpen, setMediaLibraryOpen] = useState(false);
  const [mediaLibraryCallback, setMediaLibraryCallback] = useState(null);
  const [previewModule, setPreviewModule] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const res = await axios.get(`${API}/organizations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAvailableOrgs(res.data.organizations || []);
      } catch (err) {
        // silent fail
      }
    };
    if (token) fetchOrgs();
  }, [token]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    if (isEditMode) {
      fetchCourse();
    }
  }, [isAdmin, courseId]);

  const fetchCourse = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/course-builder/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourse(res.data);
      // Expand all topics by default
      const expanded = {};
      res.data.topics?.forEach(t => { expanded[t.topic_id] = true; });
      setExpandedTopics(expanded);
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  // Module Management (top-level containers)
  const addTopic = () => {
    const topicsArray = course.topics || [];
    const newTopic = {
      topic_id: `module_${Date.now()}`,
      title: `Module ${topicsArray.length + 1}`,
      description: '',
      order: topicsArray.length,
      modules: []  // This now holds "Topics" (nested items)
    };
    setCourse(prev => ({
      ...prev,
      topics: [...(prev.topics || []), newTopic]
    }));
    setExpandedTopics(prev => ({ ...prev, [newTopic.topic_id]: true }));
  };

  const updateTopic = (topicId, updates) => {
    setCourse(prev => ({
      ...prev,
      topics: prev.topics.map(t => 
        t.topic_id === topicId ? { ...t, ...updates } : t
      )
    }));
  };

  const deleteTopic = (topicId) => {
    setCourse(prev => ({
      ...prev,
      topics: prev.topics.filter(t => t.topic_id !== topicId)
    }));
  };

  // Topic Management (nested items within Modules)
  const addModule = (topicId) => {
    const topic = course.topics.find(t => t.topic_id === topicId);
    const newModule = {
      module_id: `topic_${Date.now()}`,
      title: `Topic ${(topic?.modules?.length || 0) + 1}`,
      description: '',
      order: topic?.modules?.length || 0,
      content_type: null,
      content: null
    };
    
    setCourse(prev => ({
      ...prev,
      topics: prev.topics.map(t => 
        t.topic_id === topicId 
          ? { ...t, modules: [...(t.modules || []), newModule] }
          : t
      )
    }));
    setExpandedModules(prev => ({ ...prev, [newModule.module_id]: true }));
  };

  const updateModule = (topicId, moduleId, updates) => {
    setCourse(prev => ({
      ...prev,
      topics: prev.topics.map(t => 
        t.topic_id === topicId 
          ? {
              ...t,
              modules: t.modules.map(m => 
                m.module_id === moduleId ? { ...m, ...updates } : m
              )
            }
          : t
      )
    }));
  };

  const deleteModule = (topicId, moduleId) => {
    setCourse(prev => ({
      ...prev,
      topics: prev.topics.map(t => 
        t.topic_id === topicId 
          ? { ...t, modules: t.modules.filter(m => m.module_id !== moduleId) }
          : t
      )
    }));
  };

  // Set content type for module
  const setModuleContentType = (topicId, moduleId, contentType) => {
    // Only reset content if changing to a different content type
    const topic = course.topics.find(t => t.topic_id === topicId);
    const module = topic?.modules?.find(m => m.module_id === moduleId);
    
    if (module?.content_type !== contentType) {
      updateModule(topicId, moduleId, { 
        content_type: contentType,
        content: null // Reset content when type changes
      });
    }
    setActiveContentDialog({ topicId, moduleId, contentType });
  };

  // Drag and Drop handlers
  const handleDragStart = (e, type, id, parentId = null) => {
    setDraggedItem({ type, id, parentId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropTopic = (e, targetIndex) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.type !== 'topic') return;
    
    const sourceIndex = course.topics.findIndex(t => t.topic_id === draggedItem.id);
    if (sourceIndex === targetIndex) return;
    
    const newTopics = [...course.topics];
    const [removed] = newTopics.splice(sourceIndex, 1);
    newTopics.splice(targetIndex, 0, removed);
    
    // Update order
    newTopics.forEach((t, i) => t.order = i);
    setCourse(prev => ({ ...prev, topics: newTopics }));
    setDraggedItem(null);
  };

  const handleDropModule = (e, topicId, targetIndex) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.type !== 'module') return;
    
    const sourceTopic = course.topics.find(t => t.topic_id === draggedItem.parentId);
    const targetTopic = course.topics.find(t => t.topic_id === topicId);
    
    if (!sourceTopic || !targetTopic) return;
    
    const sourceIndex = sourceTopic.modules.findIndex(m => m.module_id === draggedItem.id);
    const module = sourceTopic.modules[sourceIndex];
    
    // Same topic reorder
    if (draggedItem.parentId === topicId) {
      const newModules = [...targetTopic.modules];
      newModules.splice(sourceIndex, 1);
      newModules.splice(targetIndex, 0, module);
      newModules.forEach((m, i) => m.order = i);
      
      updateTopic(topicId, { modules: newModules });
    } else {
      // Move between topics
      const newSourceModules = sourceTopic.modules.filter(m => m.module_id !== module.module_id);
      const newTargetModules = [...targetTopic.modules];
      newTargetModules.splice(targetIndex, 0, module);
      
      newSourceModules.forEach((m, i) => m.order = i);
      newTargetModules.forEach((m, i) => m.order = i);
      
      setCourse(prev => ({
        ...prev,
        topics: prev.topics.map(t => {
          if (t.topic_id === draggedItem.parentId) return { ...t, modules: newSourceModules };
          if (t.topic_id === topicId) return { ...t, modules: newTargetModules };
          return t;
        })
      }));
    }
    
    setDraggedItem(null);
  };

  // Validation
  const validateCourse = () => {
    const errors = [];
    
    if (!course.title.trim()) errors.push('Course title is required');
    if (!course.description.trim()) errors.push('Course description is required');
    if (!course.category) errors.push('Category is required');
    if (!course.topics || course.topics.length === 0) errors.push('At least one topic is required');
    
    (course.topics || []).forEach((topic, ti) => {
      if (!topic.title.trim()) errors.push(`Topic ${ti + 1}: Title is required`);
      if (!topic.modules || topic.modules.length === 0) errors.push(`Topic "${topic.title}": At least one module is required`);
      
      (topic.modules || []).forEach((module, mi) => {
        if (!module.title.trim()) errors.push(`${topic.title} > Module ${mi + 1}: Title is required`);
        if (!module.content_type) errors.push(`${topic.title} > ${module.title}: Content type not selected`);
        if (module.content_type && !module.content) {
          errors.push(`${topic.title} > ${module.title}: Content not configured`);
        }
      });
    });
    
    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Save course
  const saveCourse = async (publish = false) => {
    if (publish && !validateCourse()) {
      toast.error('Please fix validation errors before publishing');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...course,
        is_published: publish ? true : course.is_published
      };
      
      let res;
      if (isEditMode) {
        res = await axios.put(`${API}/course-builder/${courseId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        res = await axios.post(`${API}/course-builder`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success(publish ? 'Course published successfully!' : 'Course saved as draft');
      
      if (!isEditMode && res.data.course_id) {
        navigate(`/admin/course-builder/${res.data.course_id}`);
      } else {
        setCourse(res.data);
      }
    } catch (error) {
      console.error('Error saving course:', error);
      toast.error(error.response?.data?.detail || 'Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  // Calculate completion stats
  const getCompletionStats = () => {
    let totalModules = 0;
    let configuredModules = 0;
    
    // Safely iterate over topics - handle undefined/null topics array
    (course.topics || []).forEach(topic => {
      (topic.modules || []).forEach(module => {
        totalModules++;
        if (module.content_type && module.content) {
          configuredModules++;
        }
      });
    });
    
    return {
      total: totalModules,
      configured: configuredModules,
      percent: totalModules > 0 ? Math.round((configuredModules / totalModules) * 100) : 0
    };
  };

  const stats = getCompletionStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-navigate-dark flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navigate-dark" data-testid="course-builder">
      {/* Header */}
      <header className="bg-navigate-surface border-b border-zinc-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-zinc-400 hover:text-white transition-colors">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-white">
                {isEditMode ? 'Edit Course' : 'Create New Course'}
              </h1>
              <p className="text-sm text-zinc-400">
                Build your course structure with topics, modules, and content
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Status Badge */}
            <StatusBadge status={course.is_published ? 'published' : 'draft'} />
            
            {/* Completion Progress */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800 rounded-lg">
              <div className="w-24 h-2 bg-zinc-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-navigate-gold transition-all duration-300"
                  style={{ width: `${stats.percent}%` }}
                />
              </div>
              <span className="text-xs text-zinc-400">{stats.configured}/{stats.total} modules</span>
            </div>
            
            <Button
              variant="outline"
              onClick={() => saveCourse(false)}
              disabled={saving}
              className="border-zinc-700"
              data-testid="save-draft-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Draft
            </Button>
            
            <Button
              variant="outline"
              onClick={() => setShowPreviewModal(true)}
              className="border-blue-600 text-blue-400 hover:bg-blue-600/10"
              data-testid="preview-btn"
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            
            <Button
              onClick={() => {
                if (!validateCourse()) {
                  toast.error('Please fix validation errors before publishing');
                  return;
                }
                setShowPreviewModal(true);
              }}
              disabled={saving}
              className="bg-navigate-gold text-black hover:bg-navigate-gold/90"
              data-testid="publish-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {course.is_published ? 'Update' : 'Publish'}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-12 gap-8">
          {/* Main Content - Course Structure */}
          <div className="col-span-8">
            {/* Course Info Card */}
            <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6 mb-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-navigate-gold" />
                Course Information
              </h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-zinc-400">Course Title *</Label>
                  <Input
                    value={course.title}
                    onChange={(e) => setCourse(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Advanced Compliance Management"
                    className="mt-1 bg-zinc-800 border-zinc-700"
                    data-testid="course-title-input"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label className="text-zinc-400">Description *</Label>
                  <Textarea
                    value={course.description}
                    onChange={(e) => setCourse(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe what learners will gain from this course..."
                    rows={3}
                    className="mt-1 bg-zinc-800 border-zinc-700"
                    data-testid="course-description-input"
                  />
                </div>
                
                <div>
                  <Label className="text-zinc-400">Category *</Label>
                  <Select
                    value={course.category}
                    onValueChange={(v) => setCourse(prev => ({ ...prev, category: v }))}
                  >
                    <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700" data-testid="course-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-zinc-400">Specialisation</Label>
                  <Select
                    value={course.specialisation}
                    onValueChange={(v) => setCourse(prev => ({ ...prev, specialisation: v }))}
                  >
                    <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700" data-testid="course-specialisation-select">
                      <SelectValue placeholder="Select specialisation" />
                    </SelectTrigger>
                    <SelectContent>
                      {SPECIALISATIONS.map(spec => (
                        <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-zinc-400">Level</Label>
                  <Select
                    value={course.level}
                    onValueChange={(v) => setCourse(prev => ({ ...prev, level: v }))}
                  >
                    <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700" data-testid="course-level-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-zinc-400">Price (ZAR)</Label>
                  <Input
                    type="number"
                    value={course.price}
                    onChange={(e) => setCourse(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                    className="mt-1 bg-zinc-800 border-zinc-700"
                    data-testid="course-price-input"
                  />
                </div>
                
                <div>
                  <Label className="text-zinc-400">CPD Points</Label>
                  <Input
                    type="number"
                    value={course.cpd_points}
                    onChange={(e) => setCourse(prev => ({ ...prev, cpd_points: parseInt(e.target.value) || 0 }))}
                    className="mt-1 bg-zinc-800 border-zinc-700"
                    data-testid="course-cpd-input"
                  />
                </div>
              </div>
            </div>

            {/* Modules Section - NEW HIERARCHY: Module → Topics → Content */}
            <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Layers className="h-5 w-5 text-navigate-gold" />
                  Course Structure
                </h2>
                <Button
                  onClick={addTopic}
                  size="sm"
                  className="bg-navigate-gold text-black hover:bg-navigate-gold/90"
                  data-testid="add-module-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Module
                </Button>
              </div>

              {(!course.topics || course.topics.length === 0) ? (
                <div className="text-center py-12 border-2 border-dashed border-zinc-700 rounded-lg">
                  <Layers className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-400 mb-2">No modules yet</p>
                  <p className="text-sm text-zinc-500 mb-4">Start building your course structure by adding modules</p>
                  <Button
                    onClick={addTopic}
                    variant="outline"
                    className="border-navigate-gold text-navigate-gold hover:bg-navigate-gold/10"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add First Module
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {(course.topics || []).map((topic, topicIndex) => (
                    <div
                      key={topic.topic_id}
                      className="border border-zinc-700 rounded-lg overflow-hidden"
                      draggable
                      onDragStart={(e) => handleDragStart(e, 'topic', topic.topic_id)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDropTopic(e, topicIndex)}
                      data-testid={`topic-${topicIndex}`}
                    >
                      {/* Topic Header */}
                      <div className="bg-zinc-800/50 px-4 py-3 flex items-center gap-3">
                        <GripVertical className="h-5 w-5 text-zinc-500 cursor-grab" />
                        
                        <button
                          onClick={() => setExpandedTopics(prev => ({ 
                            ...prev, 
                            [topic.topic_id]: !prev[topic.topic_id] 
                          }))}
                          className="text-zinc-400 hover:text-white"
                          data-testid={`module-expand-btn-${topicIndex}`}
                        >
                          {expandedTopics[topic.topic_id] ? (
                            <ChevronDown className="h-5 w-5" />
                          ) : (
                            <ChevronRight className="h-5 w-5" />
                          )}
                        </button>
                        
                        <div className="flex items-center gap-2 flex-1">
                          <LayoutList className="h-4 w-4 text-navigate-gold" />
                          <Input
                            value={topic.title}
                            onChange={(e) => updateTopic(topic.topic_id, { title: e.target.value })}
                            className="bg-transparent border-none p-0 h-auto text-white font-medium focus-visible:ring-0"
                            placeholder="Module title..."
                          />
                        </div>
                        
                        <span className="text-xs text-zinc-500 bg-zinc-700 px-2 py-1 rounded">
                          {topic.modules?.length || 0} topics
                        </span>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteTopic(topic.topic_id)}
                          className="text-zinc-400 hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      {/* Module Content - Topics inside */}
                      {expandedTopics[topic.topic_id] && (
                        <div className="p-4 space-y-3">
                          <Textarea
                            value={topic.description}
                            onChange={(e) => updateTopic(topic.topic_id, { description: e.target.value })}
                            placeholder="Module description (optional)..."
                            rows={2}
                            className="bg-zinc-800 border-zinc-700 text-sm"
                          />
                          
                          {/* Topics inside Module */}
                          <div className="space-y-2 mt-4">
                            <div className="text-xs text-zinc-400 uppercase tracking-wide mb-2 flex items-center gap-2">
                              <BookOpen className="h-3 w-3" />
                              Topics
                            </div>
                            {topic.modules?.map((module, moduleIndex) => (
                              <ModuleCard
                                key={module.module_id}
                                module={module}
                                moduleIndex={moduleIndex}
                                topicId={topic.topic_id}
                                onUpdate={(updates) => updateModule(topic.topic_id, module.module_id, updates)}
                                onDelete={() => deleteModule(topic.topic_id, module.module_id)}
                                onSetContentType={(type) => setModuleContentType(topic.topic_id, module.module_id, type)}
                                onDragStart={(e) => handleDragStart(e, 'module', module.module_id, topic.topic_id)}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDropModule(e, topic.topic_id, moduleIndex)}
                                expanded={expandedModules[module.module_id]}
                                onToggleExpand={() => setExpandedModules(prev => ({
                                  ...prev,
                                  [module.module_id]: !prev[module.module_id]
                                }))}
                                onPreview={() => setPreviewModule(module)}
                              />
                            ))}
                          </div>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => addModule(topic.topic_id)}
                            className="w-full border-dashed border-zinc-600 text-zinc-400 hover:text-white hover:border-zinc-500"
                            data-testid={`add-topic-btn-${topicIndex}`}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Topic
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Validation & Settings */}
          <div className="col-span-4 space-y-6">
            {/* Quick Actions */}
            <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-4">
              <h3 className="text-sm font-medium text-white mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start border-zinc-700 text-zinc-300"
                  onClick={() => setMediaLibraryOpen(true)}
                  data-testid="open-media-library-btn"
                >
                  <FolderOpen className="h-4 w-4 mr-2 text-navigate-gold" />
                  Media Library
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start border-zinc-700 text-zinc-300"
                  onClick={() => navigate('/ai-course/create')}
                >
                  <Sparkles className="h-4 w-4 mr-2 text-[#1EBFB3]" />
                  AI Course Generator
                </Button>
                {isEditMode && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start border-zinc-700 text-zinc-300"
                    onClick={() => window.open(`/courses/${courseId}`, '_blank')}
                  >
                    <Eye className="h-4 w-4 mr-2 text-blue-400" />
                    Preview Course
                  </Button>
                )}
              </div>
            </div>

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <h3 className="text-sm font-medium text-red-400 mb-3 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Validation Errors
                </h3>
                <ul className="space-y-1">
                  {validationErrors.map((error, i) => (
                    <li key={i} className="text-xs text-red-300 flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">•</span>
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Course Settings */}
            <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-4">
              <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                <Settings className="h-4 w-4 text-zinc-400" />
                Course Settings
              </h3>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-zinc-400">Instructor Name</Label>
                  <Input
                    value={course.instructor_name}
                    onChange={(e) => setCourse(prev => ({ ...prev, instructor_name: e.target.value }))}
                    placeholder="e.g., Dr. John Smith"
                    className="mt-1 bg-zinc-800 border-zinc-700 text-sm"
                  />
                </div>
                
                <div>
                  <Label className="text-xs text-zinc-400">Duration (hours)</Label>
                  <Input
                    type="number"
                    value={course.duration_hours}
                    onChange={(e) => setCourse(prev => ({ ...prev, duration_hours: parseFloat(e.target.value) || 1 }))}
                    className="mt-1 bg-zinc-800 border-zinc-700 text-sm"
                  />
                </div>
                
                {/* Course Image Manager */}
                <CourseImageManager
                  currentImageUrl={course.thumbnail_url}
                  onImageSelect={(url) => setCourse(prev => ({ ...prev, thumbnail_url: url }))}
                  courseTitle={course.title}
                  courseDescription={course.description}
                  token={token}
                />

                {/* Visible on Master Platform Toggle */}
                <div className="pt-3 border-t border-zinc-700">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-zinc-400 flex items-center gap-2">
                      <Globe className="h-3 w-3 text-blue-400" />
                      Visible on Cerebro Master
                    </Label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={course.visible_on_master !== false}
                        onChange={(e) => setCourse(prev => ({ ...prev, visible_on_master: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">
                    When enabled, this course appears in the master catalog. Turn off to hide it on master while sending only to client tenants.
                  </p>
                </div>

                {/* Publish to All Organizations */}
                <div className="pt-3 border-t border-zinc-700">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-zinc-400 flex items-center gap-2">
                      <Building2 className="h-3 w-3 text-amber-400" />
                      Publish to All Organizations
                    </Label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={course.publish_to_all_orgs || false}
                        onChange={(e) => setCourse(prev => ({ ...prev, publish_to_all_orgs: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-500"></div>
                    </label>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">
                    When enabled, this course will be visible in the catalog for all tenant organizations.
                  </p>
                </div>

                {/* Specific Client Tenants Selector */}
                {!course.publish_to_all_orgs && availableOrgs.length > 0 && (
                  <div className="pt-3 border-t border-zinc-700">
                    <Label className="text-xs text-zinc-400 block mb-2">
                      Send to Specific Client Tenants ({course.publish_to_tenants?.length || 0})
                    </Label>
                    <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                      {availableOrgs.map(org => {
                        const isSelected = (course.publish_to_tenants || []).includes(org.org_id);
                        return (
                          <label key={org.org_id} className="flex items-center justify-between text-xs text-zinc-300 hover:text-white cursor-pointer bg-zinc-800/60 p-2 rounded border border-zinc-800 hover:border-zinc-700">
                            <div className="flex items-center gap-2 min-w-0">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {
                                  const current = course.publish_to_tenants || [];
                                  const updated = isSelected 
                                    ? current.filter(id => id !== org.org_id)
                                    : [...current, org.org_id];
                                  setCourse(prev => ({ ...prev, publish_to_tenants: updated }));
                                }}
                                className="rounded border-zinc-700 text-amber-500 focus:ring-0"
                              />
                              <span className="truncate">{org.name}</span>
                            </div>
                            {isSelected && (
                              <span className="text-[10px] text-amber-400 font-medium px-1.5 py-0.5 rounded bg-amber-500/10 shrink-0">
                                Assigned
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Content Type Legend */}
            <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-4">
              <h3 className="text-sm font-medium text-white mb-3">Content Types</h3>
              <div className="space-y-2">
                {Object.entries(CONTENT_TYPES).map(([key, config]) => (
                  <div key={key} className="flex items-center gap-2">
                    <config.icon className={`h-4 w-4 ${config.color}`} />
                    <span className="text-xs text-zinc-400">{config.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Configuration Dialog */}
      <ContentConfigDialog
        open={!!activeContentDialog}
        onClose={() => setActiveContentDialog(null)}
        contentType={activeContentDialog?.contentType}
        courseId={courseId || course.course_id}
        topicId={activeContentDialog?.topicId}
        moduleId={activeContentDialog?.moduleId}
        currentContent={
          activeContentDialog 
            ? course.topics
                .find(t => t.topic_id === activeContentDialog.topicId)
                ?.modules?.find(m => m.module_id === activeContentDialog.moduleId)
                ?.content
            : null
        }
        onSave={(content) => {
          if (activeContentDialog) {
            updateModule(activeContentDialog.topicId, activeContentDialog.moduleId, { content });
          }
          setActiveContentDialog(null);
        }}
        token={token}
        onOpenMediaLibrary={(callback) => {
          setMediaLibraryCallback(() => callback);
          setMediaLibraryOpen(true);
        }}
      />

      {/* Media Library Dialog */}
      <MediaLibraryDialog
        open={mediaLibraryOpen}
        onClose={() => {
          setMediaLibraryOpen(false);
          setMediaLibraryCallback(null);
        }}
        onSelect={(url) => {
          if (mediaLibraryCallback) {
            mediaLibraryCallback(url);
          }
          setMediaLibraryOpen(false);
          setMediaLibraryCallback(null);
        }}
        token={token}
      />

      {/* Module Preview Dialog */}
      <ModulePreviewDialog
        open={!!previewModule}
        onClose={() => setPreviewModule(null)}
        module={previewModule}
        courseName={course.title}
      />

      {/* Course Preview Modal - Required before publishing */}
      <ContentPreviewModal
        open={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        contentType="course"
        content={course}
        token={token}
        onPublish={async () => {
          await saveCourse(true);
        }}
        onSaveDraft={async () => {
          await saveCourse(false);
        }}
        onStartPreview={() => {
          // Navigate to course player in preview mode
          const playerUrl = course.ai_generated || course.course_id?.startsWith('ai_course')
            ? `/ai-course/${course.course_id}?preview=true`
            : `/courses/${course.course_id}/learn?preview=true`;
          window.open(playerUrl, '_blank');
          setShowPreviewModal(false);
        }}
      />
    </div>
  );
}

// Module Card Component
function ModuleCard({ 
  module, 
  moduleIndex, 
  topicId,
  onUpdate, 
  onDelete, 
  onSetContentType,
  onDragStart,
  onDragOver,
  onDrop,
  expanded,
  onToggleExpand,
  onPreview
}) {
  const contentConfig = module.content_type ? CONTENT_TYPES[module.content_type] : null;
  
  // Check if module content is actually configured (not just an empty object)
  const isContentConfigured = (mod) => {
    if (!mod.content || typeof mod.content !== 'object') return false;
    
    // Check based on content type
    switch (mod.content_type) {
      case 'video':
        return !!mod.content.url;
      case 'embed':
        return !!(mod.content.url || mod.content.embed_code);
      case 'document':
        return !!mod.content.url;
      case 'quiz':
        return !!mod.content.quiz_id;
      case 'ai_generated':
        return !!(mod.content.generated_content || mod.content.content_id);
      case 'learning_lab':
        return !!mod.content.lab_id;
      case 'existing_course':
        return !!mod.content.linked_course_id;
      default:
        // For unknown types, check if content has any keys
        return Object.keys(mod.content).length > 0;
    }
  };
  
  return (
    <div
      className={`border rounded-lg overflow-hidden transition-all ${
        contentConfig 
          ? `${contentConfig.borderColor} ${contentConfig.bgColor}` 
          : 'border-zinc-700 bg-zinc-800/30'
      }`}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      data-testid={`module-${moduleIndex}`}
    >
      <div className="px-3 py-2 flex items-center gap-2">
        <GripVertical className="h-4 w-4 text-zinc-500 cursor-grab" />
        
        <button onClick={onToggleExpand} className="text-zinc-400 hover:text-white">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        
        {contentConfig && (
          <contentConfig.icon className={`h-4 w-4 ${contentConfig.color}`} />
        )}
        
        <Input
          value={module.title}
          onChange={(e) => onUpdate({ title: e.target.value })}
          className="bg-transparent border-none p-0 h-auto text-sm text-white focus-visible:ring-0 flex-1"
          placeholder="Topic title..."
        />
        
        {isContentConfigured(module) && (
          <>
            <CheckCircle className="h-4 w-4 text-green-400" />
            <Button
              variant="ghost"
              size="sm"
              onClick={onPreview}
              className="h-6 w-6 p-0 text-zinc-400 hover:text-blue-400"
              title="Preview module"
            >
              <Eye className="h-3 w-3" />
            </Button>
          </>
        )}
        
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="h-6 w-6 p-0 text-zinc-400 hover:text-red-400"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
      
      {expanded && (
        <div className="px-3 pb-3 space-y-3">
          <Textarea
            value={module.description || ''}
            onChange={(e) => onUpdate({ description: e.target.value })}
            placeholder="Topic description (optional)..."
            rows={2}
            className="bg-zinc-900/50 border-zinc-600 text-xs"
          />
          
          {/* Content Type Selector */}
          <div>
            <Label className="text-xs text-zinc-400 mb-2 block">Content Type</Label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(CONTENT_TYPES).map(([type, config]) => (
                <button
                  key={type}
                  onClick={() => onSetContentType(type)}
                  className={`p-2 rounded-lg border text-center transition-all ${
                    module.content_type === type
                      ? `${config.borderColor} ${config.bgColor}`
                      : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                  }`}
                  data-testid={`content-type-${type}`}
                >
                  <config.icon className={`h-5 w-5 mx-auto mb-1 ${
                    module.content_type === type ? config.color : 'text-zinc-500'
                  }`} />
                  <span className={`text-xs ${
                    module.content_type === type ? 'text-white' : 'text-zinc-400'
                  }`}>
                    {config.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Content Status */}
          {module.content_type && (
            <div className={`p-2 rounded-lg border ${
              isContentConfigured(module)
                ? 'border-green-500/30 bg-green-500/10' 
                : 'border-yellow-500/30 bg-yellow-500/10'
            }`}>
              <div className="flex items-center gap-2">
                {isContentConfigured(module) ? (
                  <>
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    <span className="text-xs text-green-300">Content configured</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-4 w-4 text-yellow-400" />
                    <span className="text-xs text-yellow-300">Click to configure content</span>
                  </>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSetContentType(module.content_type)}
                  className="ml-auto h-6 text-xs"
                  data-testid="configure-content-btn"
                >
                  {isContentConfigured(module) ? 'Edit' : 'Configure'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Content Configuration Dialog
function ContentConfigDialog({ 
  open, 
  onClose, 
  contentType, 
  topicId, 
  moduleId,
  courseId,
  currentContent,
  onSave,
  token,
  onOpenMediaLibrary
}) {
  const [content, setContent] = useState(currentContent || {});
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  const config = contentType ? CONTENT_TYPES[contentType] : null;
  
  useEffect(() => {
    setContent(currentContent || {});
  }, [currentContent, open]);
  
  const handleFileUpload = async (e, fileType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);
    
    try {
      const res = await axios.post(`${API_BASE}/api/media/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setContent(prev => ({ ...prev, url: res.data.url, filename: file.name, media_id: res.data.id }));
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateAIContent = async () => {
    if (!content.prompt) {
      toast.error('Please enter a content prompt');
      return;
    }
    
    setGenerating(true);
    try {
      const res = await axios.post(`${API_BASE}/api/course-builder/content/ai-generate`, {
        prompt: content.prompt,
        difficulty: content.difficulty || 'intermediate',
        include_quiz: content.include_quiz || false,
        course_id: courseId,
        topic_id: topicId,
        module_id: moduleId,
        module_title: content.title || 'Module'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setContent(prev => ({
        ...prev,
        content_id: res.data.content_id,
        generated_content: res.data.content,
        quiz: res.data.quiz,
        status: 'generated'
      }));
      toast.success('AI content generated successfully!');
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate AI content');
    } finally {
      setGenerating(false);
    }
  };

  const handleCreateQuiz = async () => {
    if (!content.quiz_context) {
      toast.error('Please enter quiz topic/context');
      return;
    }
    
    setGenerating(true);
    try {
      const res = await axios.post(`${API_BASE}/api/course-builder/content/quiz`, {
        generation_method: content.generation_method || 'ai',
        course_id: courseId,
        topic_id: topicId,
        module_id: moduleId,
        quiz_context: content.quiz_context,
        num_questions: content.num_questions || 5,
        pass_threshold: content.pass_threshold || 70,
        randomize: content.randomize || false
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setContent(prev => ({
        ...prev,
        quiz_id: res.data.quiz_id,
        questions: res.data.questions,
        question_count: res.data.question_count,
        status: 'created'
      }));
      toast.success(`Quiz created with ${res.data.question_count} questions!`);
    } catch (error) {
      console.error('Quiz creation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create quiz');
    } finally {
      setGenerating(false);
    }
  };
  
  const renderContentForm = () => {
    switch (contentType) {
      case 'ai_generated':
        return (
          <div className="space-y-4">
            <div>
              <Label>Content Prompt</Label>
              <Textarea
                value={content.prompt || ''}
                onChange={(e) => setContent(prev => ({ ...prev, prompt: e.target.value }))}
                placeholder="Describe what content should be generated for this module..."
                rows={4}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label>Difficulty Level</Label>
              <Select
                value={content.difficulty || 'intermediate'}
                onValueChange={(v) => setContent(prev => ({ ...prev, difficulty: v }))}
              >
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={content.include_quiz || false}
                onChange={(e) => setContent(prev => ({ ...prev, include_quiz: e.target.checked }))}
                className="rounded border-zinc-600"
              />
              <Label className="text-sm">Include auto-generated quiz</Label>
            </div>
            
            {/* Generate Button */}
            <Button
              onClick={handleGenerateAIContent}
              disabled={generating || !content.prompt}
              className="w-full bg-[#1EBFB3] hover:bg-purple-700"
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate AI Content
                </>
              )}
            </Button>
            
            {/* Generated Content Preview */}
            {content.generated_content && (
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-green-400 text-sm mb-2">
                  <CheckCircle className="h-4 w-4" />
                  Content Generated Successfully
                </div>
                <p className="text-xs text-zinc-400">
                  {content.generated_content.sections?.length || 0} sections created
                  {content.quiz && ` + ${content.quiz.questions?.length || 0} quiz questions`}
                </p>
              </div>
            )}
          </div>
        );
        
      case 'video':
        return (
          <div className="space-y-4">
            <div>
              <Label>Video Source</Label>
              <Select
                value={content.source_type || 'upload'}
                onValueChange={(v) => setContent(prev => ({ ...prev, source_type: v }))}
              >
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="upload">Upload Video</SelectItem>
                  <SelectItem value="url">External URL</SelectItem>
                  <SelectItem value="media_library">Media Library</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {content.source_type === 'upload' && (
              <div>
                <div className="mt-2 border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept="video/*"
                    onChange={(e) => handleFileUpload(e, 'video')}
                    className="hidden"
                    id="video-upload"
                    disabled={uploading}
                  />
                  <label htmlFor="video-upload" className="cursor-pointer">
                    {uploading ? (
                      <Loader2 className="h-8 w-8 animate-spin text-navigate-gold mx-auto mb-2" />
                    ) : (
                      <Video className="h-8 w-8 text-zinc-500 mx-auto mb-2" />
                    )}
                    <p className="text-sm text-zinc-400">
                      {content.filename || 'Click to upload video'}
                    </p>
                  </label>
                </div>
              </div>
            )}
            
            {content.source_type === 'url' && (
              <div>
                <Label>Video URL</Label>
                <Input
                  value={content.url || ''}
                  onChange={(e) => setContent(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://..."
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
              </div>
            )}
            
            {content.source_type === 'media_library' && (
              <Button
                variant="outline"
                onClick={() => onOpenMediaLibrary((url) => setContent(prev => ({ ...prev, url })))}
                className="w-full border-zinc-700"
              >
                <FolderOpen className="h-4 w-4 mr-2" />
                Select from Media Library
              </Button>
            )}
            
            {content.url && (
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-green-400 text-sm">
                  <CheckCircle className="h-4 w-4" />
                  Video configured
                </div>
              </div>
            )}
          </div>
        );
        
      case 'embed':
        return (
          <div className="space-y-4">
            <div>
              <Label>Embed URL</Label>
              <Input
                value={content.url || ''}
                onChange={(e) => setContent(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://youtube.com/watch?v=..."
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Supports YouTube, Vimeo, and other embeddable content
              </p>
            </div>
            <div>
              <Label>Embed Code (Optional)</Label>
              <Textarea
                value={content.embed_code || ''}
                onChange={(e) => setContent(prev => ({ ...prev, embed_code: e.target.value }))}
                placeholder="<iframe>...</iframe>"
                rows={3}
                className="mt-1 bg-zinc-800 border-zinc-700 font-mono text-xs"
              />
            </div>
          </div>
        );
        
      case 'document':
        return (
          <div className="space-y-4">
            <div>
              <Label>Upload Document</Label>
              <div className="mt-2 border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.ppt,.pptx"
                  onChange={(e) => handleFileUpload(e, 'document')}
                  className="hidden"
                  id="doc-upload"
                  disabled={uploading}
                />
                <label htmlFor="doc-upload" className="cursor-pointer">
                  {uploading ? (
                    <Loader2 className="h-8 w-8 animate-spin text-navigate-gold mx-auto mb-2" />
                  ) : (
                    <FileText className="h-8 w-8 text-zinc-500 mx-auto mb-2" />
                  )}
                  <p className="text-sm text-zinc-400">
                    {content.filename || 'Click to upload document'}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">PDF, Word, PowerPoint</p>
                </label>
              </div>
            </div>
            {content.url && (
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-green-400 text-sm">
                  <CheckCircle className="h-4 w-4" />
                  Document uploaded: {content.filename}
                </div>
              </div>
            )}
          </div>
        );
        
      case 'quiz':
        return (
          <div className="space-y-4">
            {/* Mode Selection */}
            <div>
              <Label>Quiz Source</Label>
              <Select
                value={content.quiz_source || 'create'}
                onValueChange={(v) => setContent(prev => ({ ...prev, quiz_source: v, quiz_id: null }))}
              >
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="create">Create New Quiz (AI)</SelectItem>
                  <SelectItem value="existing">Select from Quiz Bank</SelectItem>
                  <SelectItem value="manual">Manual Creation</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Create New Quiz with AI */}
            {(content.quiz_source === 'create' || !content.quiz_source) && (
              <CreateQuizForm
                token={token}
                content={content}
                setContent={setContent}
                generating={generating}
                setGenerating={setGenerating}
                courseId={courseId}
                topicId={topicId}
                moduleId={moduleId}
              />
            )}

            {/* Select from Existing Quiz Bank */}
            {content.quiz_source === 'existing' && (
              <>
                <div>
                  <Label>Select from Quiz Bank</Label>
                  <p className="text-xs text-zinc-500 mt-1 mb-3">
                    Choose an existing quiz from the quiz bank
                  </p>
                  <QuizBankSelector
                    token={token}
                    selectedQuizId={content.quiz_id}
                    onSelect={(quiz) => setContent(prev => ({
                      ...prev,
                      quiz_id: quiz.quiz_id,
                      quiz_title: quiz.title,
                      question_count: quiz.question_count
                    }))}
                  />
                </div>
                {content.quiz_id && (
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <div className="flex items-center gap-2 text-yellow-400 text-sm">
                      <CheckCircle className="h-4 w-4" />
                      Quiz Selected: {content.question_count} questions
                    </div>
                    <p className="text-xs text-zinc-400 mt-1">{content.quiz_title}</p>
                  </div>
                )}
              </>
            )}

            {/* Manual Quiz Creation */}
            {content.quiz_source === 'manual' && (
              <div className="text-center py-6 border border-dashed border-zinc-700 rounded-lg">
                <HelpCircle className="h-8 w-8 text-zinc-500 mx-auto mb-2" />
                <p className="text-sm text-zinc-400">Manual quiz editor</p>
                <p className="text-xs text-zinc-500 mt-1">Coming soon - Use AI generation for now</p>
              </div>
            )}
          </div>
        );
      
      case 'learning_lab':
        return (
          <div className="space-y-4">
            {/* Mode Selection */}
            <div>
              <Label>Learning Lab Source</Label>
              <Select
                value={content.lab_source || 'existing'}
                onValueChange={(v) => setContent(prev => ({ ...prev, lab_source: v, lab_id: null, lab_title: null }))}
              >
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="existing">Select Existing Lab</SelectItem>
                  <SelectItem value="create">Create New Lab</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Existing Lab Selection */}
            {(content.lab_source === 'existing' || !content.lab_source) && (
              <>
                <div>
                  <Label>Select Learning Lab</Label>
                  <p className="text-xs text-zinc-500 mt-1 mb-3">
                    Add a simulation-based Learning Lab to this topic
                  </p>
                  <LearningLabSelector 
                    token={token}
                    selectedLabId={content.lab_id}
                    onSelect={(lab) => setContent(prev => ({ 
                      ...prev, 
                      lab_id: lab.lab_id,
                      lab_title: lab.title,
                      lab_description: lab.description,
                      lab_type: lab.scenario_type
                    }))}
                  />
                </div>
                
                {content.lab_id && (
                  <div className="p-3 bg-teal-500/10 border border-teal-500/30 rounded-lg">
                    <div className="flex items-center gap-2 text-teal-400 text-sm">
                      <CheckCircle className="h-4 w-4" />
                      Learning Lab Linked
                    </div>
                    <p className="text-xs text-zinc-400 mt-1">{content.lab_title}</p>
                  </div>
                )}
              </>
            )}

            {/* Create New Lab */}
            {content.lab_source === 'create' && (
              <CreateLearningLabForm
                token={token}
                content={content}
                setContent={setContent}
                generating={generating}
                setGenerating={setGenerating}
              />
            )}
          </div>
        );
      
      case 'existing_course':
        return (
          <div className="space-y-4">
            <div>
              <Label>Link Existing Course</Label>
              <p className="text-xs text-zinc-500 mt-1 mb-3">
                Reference another course as prerequisite or supplementary content
              </p>
              <ExistingCourseSelector 
                token={token}
                selectedCourseId={content.linked_course_id}
                currentCourseId={courseId}
                onSelect={(course) => setContent(prev => ({ 
                  ...prev, 
                  linked_course_id: course.course_id,
                  linked_course_title: course.title,
                  linked_course_thumbnail: course.thumbnail_url,
                  link_type: content.link_type || 'supplementary'
                }))}
              />
            </div>
            
            <div>
              <Label>Link Type</Label>
              <Select
                value={content.link_type || 'supplementary'}
                onValueChange={(v) => setContent(prev => ({ ...prev, link_type: v }))}
              >
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="prerequisite">Prerequisite (must complete first)</SelectItem>
                  <SelectItem value="supplementary">Supplementary (recommended)</SelectItem>
                  <SelectItem value="advanced">Advanced (follow-up course)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {content.linked_course_id && (
              <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-cyan-400 text-sm">
                  <CheckCircle className="h-4 w-4" />
                  Course Linked ({content.link_type})
                </div>
                <p className="text-xs text-zinc-400 mt-1">{content.linked_course_title}</p>
              </div>
            )}
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-navigate-surface border-zinc-700 max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            {config && <config.icon className={`h-5 w-5 ${config.color}`} />}
            Configure {config?.label || 'Content'}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {config?.description}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          {renderContentForm()}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-700">
            Cancel
          </Button>
          <Button 
            onClick={() => onSave(content)}
            className="bg-navigate-gold text-black hover:bg-navigate-gold/90"
          >
            Save Content
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Media Library Dialog
function MediaLibraryDialog({ open, onClose, onSelect, token }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('all');
  
  useEffect(() => {
    if (open) {
      fetchMedia();
    }
  }, [open]);
  
  const fetchMedia = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMedia(res.data);
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        await axios.post(`${API_BASE}/api/media/upload`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    fetchMedia();
    toast.success('Files uploaded successfully');
  };
  
  const filteredMedia = filter === 'all' 
    ? media 
    : media.filter(m => m.type === filter);
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-navigate-surface border-zinc-700 max-w-4xl max-h-[80vh]" aria-describedby="media-library-description">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <FolderOpen className="h-5 w-5 text-navigate-gold" />
            Media Library
          </DialogTitle>
          <DialogDescription id="media-library-description" className="text-zinc-400">
            Browse and upload media files for your courses
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-2">
            {['all', 'image', 'video', 'document'].map(f => (
              <Button
                key={f}
                variant={filter === f ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(f)}
                className={filter === f ? 'bg-navigate-gold text-black' : 'border-zinc-700'}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </Button>
            ))}
          </div>
          
          <div>
            <input
              type="file"
              multiple
              onChange={handleUpload}
              className="hidden"
              id="media-library-upload"
              disabled={uploading}
            />
            <label htmlFor="media-library-upload">
              <Button asChild disabled={uploading} className="bg-navigate-gold text-black">
                <span>
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Upload Files
                </span>
              </Button>
            </label>
          </div>
        </div>
        
        <div className="overflow-y-auto max-h-[50vh] border border-zinc-700 rounded-lg p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
            </div>
          ) : filteredMedia.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">No media files yet</p>
              <p className="text-sm text-zinc-500">Upload files to get started</p>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-4">
              {filteredMedia.map((item) => (
                <button
                  key={item.id}
                  onClick={() => onSelect(item.url)}
                  className="group relative aspect-square bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700 hover:border-navigate-gold transition-colors"
                >
                  {item.type === 'image' ? (
                    <img 
                      src={item.thumbnail_url || item.url} 
                      alt={item.filename}
                      className="w-full h-full object-cover"
                    />
                  ) : item.type === 'video' ? (
                    <div className="w-full h-full flex items-center justify-center bg-zinc-900">
                      <Video className="h-8 w-8 text-zinc-500" />
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-zinc-900">
                      <FileText className="h-8 w-8 text-zinc-500" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-navigate-gold" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/80 px-2 py-1">
                    <p className="text-xs text-zinc-300 truncate">{item.filename}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// Module Preview Dialog - Shows how learners will see the content
function ModulePreviewDialog({ open, onClose, module, courseName }) {
  if (!module) return null;
  
  const contentConfig = module.content_type ? CONTENT_TYPES[module.content_type] : null;
  const content = module.content || {};
  
  const renderPreviewContent = () => {
    switch (module.content_type) {
      case 'ai_generated':
        if (!content.generated_content) {
          return (
            <div className="text-center py-8 text-zinc-400">
              <Brain className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>AI content not yet generated</p>
              <p className="text-sm">Click &quot;Generate AI Content&quot; in the module configuration</p>
            </div>
          );
        }
        return (
          <div className="prose prose-invert max-w-none">
            <h2 className="text-xl font-bold text-white mb-4">{content.generated_content.title}</h2>
            <p className="text-zinc-300 mb-6">{content.generated_content.introduction}</p>
            
            {content.generated_content.sections?.map((section, idx) => (
              <div key={idx} className="mb-6">
                <h3 className="text-lg font-semibold text-navigate-gold mb-2">{section.heading}</h3>
                <p className="text-zinc-300 whitespace-pre-wrap">{section.content}</p>
                {section.key_points && (
                  <ul className="mt-3 space-y-1">
                    {section.key_points.map((point, i) => (
                      <li key={i} className="flex items-start gap-2 text-zinc-400">
                        <span className="text-navigate-gold mt-1">•</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
            
            {content.generated_content.summary && (
              <div className="bg-navigate-gold/10 border border-navigate-gold/30 rounded-lg p-4 mt-6">
                <h4 className="font-semibold text-navigate-gold mb-2">Summary</h4>
                <p className="text-zinc-300">{content.generated_content.summary}</p>
              </div>
            )}
            
            {content.generated_content.key_takeaways && (
              <div className="mt-6">
                <h4 className="font-semibold text-white mb-2">Key Takeaways</h4>
                <ul className="space-y-2">
                  {content.generated_content.key_takeaways.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-zinc-300">
                      <CheckCircle className="h-4 w-4 text-green-400 mt-1 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {content.quiz && (
              <div className="mt-8 border-t border-zinc-700 pt-6">
                <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-yellow-400" />
                  Module Quiz ({content.quiz.questions?.length || 0} questions)
                </h4>
                <p className="text-zinc-400 text-sm">Quiz will appear here after completing the content.</p>
              </div>
            )}
          </div>
        );
        
      case 'video':
        return (
          <div className="text-center">
            {content.url ? (
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                {content.source_type === 'url' || content.url.includes('youtube') || content.url.includes('vimeo') ? (
                  <iframe
                    src={content.url.replace('watch?v=', 'embed/')}
                    className="w-full h-full"
                    allowFullScreen
                    title="Video preview"
                  />
                ) : (
                  <video
                    src={content.url}
                    controls
                    className="w-full h-full"
                  >
                    Your browser does not support video playback.
                  </video>
                )}
              </div>
            ) : (
              <div className="py-8 text-zinc-400">
                <Video className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No video configured</p>
              </div>
            )}
          </div>
        );
        
      case 'embed':
        return (
          <div>
            {content.embed_code ? (
              <div 
                className="aspect-video bg-black rounded-lg overflow-hidden"
                dangerouslySetInnerHTML={{ __html: content.embed_code }}
              />
            ) : content.url ? (
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                <iframe
                  src={content.url}
                  className="w-full h-full"
                  allowFullScreen
                  title="Embedded content"
                />
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-400">
                <LinkIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No embedded content configured</p>
              </div>
            )}
          </div>
        );
        
      case 'document':
        return (
          <div className="text-center">
            {content.url ? (
              <div className="border border-zinc-700 rounded-lg p-6">
                <FileText className="h-16 w-16 mx-auto mb-4 text-orange-400" />
                <p className="text-white font-medium mb-2">{content.filename || 'Document'}</p>
                <a 
                  href={content.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-navigate-gold hover:underline"
                >
                  Open Document
                </a>
              </div>
            ) : (
              <div className="py-8 text-zinc-400">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No document uploaded</p>
              </div>
            )}
          </div>
        );
        
      case 'quiz':
        return (
          <div>
            {content.quiz_id ? (
              <div className="border border-zinc-700 rounded-lg p-6">
                <HelpCircle className="h-12 w-12 mx-auto mb-4 text-yellow-400" />
                <p className="text-white font-medium mb-2">Module Quiz</p>
                <p className="text-zinc-400 mb-4">
                  {content.question_count || content.num_questions || 5} questions • 
                  Pass threshold: {content.pass_threshold || 70}%
                </p>
                
                {content.questions && content.questions.length > 0 && (
                  <div className="text-left mt-6 space-y-4">
                    {content.questions.slice(0, 2).map((q, i) => (
                      <div key={i} className="bg-zinc-800/50 rounded-lg p-4">
                        <p className="text-sm text-zinc-300 mb-2">
                          <span className="text-navigate-gold font-medium">Q{i + 1}:</span> {q.question_text || q.question}
                        </p>
                        <p className="text-xs text-zinc-500">
                          {q.options?.length || 4} options
                        </p>
                      </div>
                    ))}
                    {content.questions.length > 2 && (
                      <p className="text-sm text-zinc-500 text-center">
                        + {content.questions.length - 2} more questions
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-400">
                <HelpCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Quiz not yet created</p>
                <p className="text-sm">Configure the quiz in the module settings</p>
              </div>
            )}
          </div>
        );
        
      default:
        return (
          <div className="text-center py-8 text-zinc-400">
            <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No content type selected</p>
            <p className="text-sm">Select a content type in the module settings</p>
          </div>
        );
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-navigate-dark border-zinc-700 max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="border-b border-zinc-800 pb-4">
          <div className="flex items-center gap-3">
            {contentConfig && (
              <div className={`p-2 rounded-lg ${contentConfig.bgColor}`}>
                <contentConfig.icon className={`h-5 w-5 ${contentConfig.color}`} />
              </div>
            )}
            <div>
              <DialogTitle className="text-white text-lg">
                {module.title || 'Module Preview'}
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                Preview how learners will see this {contentConfig?.label || 'content'}
              </DialogDescription>
            </div>
          </div>
          {courseName && (
            <p className="text-xs text-zinc-500 mt-2">Course: {courseName}</p>
          )}
        </DialogHeader>
        
        {/* Learner View Simulation */}
        <div className="flex-1 overflow-y-auto p-6 bg-zinc-900/50">
          {/* Simulated Learner Header */}
          <div className="bg-navigate-surface border border-zinc-800 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-medium">{module.title}</h3>
                {module.description && (
                  <p className="text-sm text-zinc-400 mt-1">{module.description}</p>
                )}
              </div>
              {content.estimated_duration_minutes && (
                <div className="flex items-center gap-1 text-zinc-400 text-sm">
                  <Clock className="h-4 w-4" />
                  {content.estimated_duration_minutes} min
                </div>
              )}
            </div>
          </div>
          
          {/* Content Preview */}
          <div className="bg-navigate-surface border border-zinc-800 rounded-lg p-6">
            {renderPreviewContent()}
          </div>
        </div>
        
        <DialogFooter className="border-t border-zinc-800 pt-4">
          <Button variant="outline" onClick={onClose} className="border-zinc-700">
            Close Preview
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Learning Lab Selector Component
function LearningLabSelector({ token, selectedLabId, onSelect }) {
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchLabs();
  }, []);

  const fetchLabs = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/labs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLabs(res.data.labs || res.data || []);
    } catch (error) {
      console.error('Error fetching labs:', error);
      // Silently fail - labs endpoint might not exist yet
      setLabs([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredLabs = labs.filter(lab => 
    lab.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lab.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-teal-400" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Input
        placeholder="Search labs..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="bg-zinc-800 border-zinc-700"
      />
      
      <div className="max-h-60 overflow-y-auto space-y-2">
        {filteredLabs.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-4">No Learning Labs found</p>
        ) : (
          filteredLabs.map((lab) => (
            <button
              key={lab.lab_id}
              onClick={() => onSelect(lab)}
              className={`w-full p-3 rounded-lg border text-left transition-all ${
                selectedLabId === lab.lab_id
                  ? 'border-teal-500 bg-teal-500/10'
                  : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
              }`}
            >
              <div className="flex items-start gap-3">
                <FlaskConical className={`h-5 w-5 mt-0.5 ${
                  selectedLabId === lab.lab_id ? 'text-teal-400' : 'text-zinc-500'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{lab.title}</p>
                  <p className="text-xs text-zinc-400 line-clamp-2 mt-1">{lab.description}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded">
                      {lab.scenario_type || 'Simulation'}
                    </span>
                    {lab.difficulty && (
                      <span className="text-xs bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded">
                        {lab.difficulty}
                      </span>
                    )}
                  </div>
                </div>
                {selectedLabId === lab.lab_id && (
                  <CheckCircle className="h-5 w-5 text-teal-400 flex-shrink-0" />
                )}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

// Existing Course Selector Component
function ExistingCourseSelector({ token, selectedCourseId, currentCourseId, onSelect }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out the current course and only show published courses
      const allCourses = res.data.courses || res.data || [];
      setCourses(allCourses.filter(c => 
        c.course_id !== currentCourseId && 
        (c.status === 'published' || c.is_published)
      ));
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = courses.filter(course => 
    course.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Input
        placeholder="Search courses..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="bg-zinc-800 border-zinc-700"
      />
      
      <div className="max-h-60 overflow-y-auto space-y-2">
        {filteredCourses.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-4">No courses found</p>
        ) : (
          filteredCourses.map((course) => (
            <button
              key={course.course_id}
              onClick={() => onSelect(course)}
              className={`w-full p-3 rounded-lg border text-left transition-all ${
                selectedCourseId === course.course_id
                  ? 'border-cyan-500 bg-cyan-500/10'
                  : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
              }`}
            >
              <div className="flex items-start gap-3">
                {course.thumbnail_url ? (
                  <img 
                    src={getThumbnailUrl(course.thumbnail_url)}
                    alt={course.title}
                    className="w-12 h-12 rounded object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded bg-zinc-700 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="h-5 w-5 text-zinc-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{course.title}</p>
                  <p className="text-xs text-zinc-400 truncate mt-0.5">{course.category}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {course.cpd_points > 0 && (
                      <span className="text-xs bg-navigate-gold/20 text-navigate-gold px-2 py-0.5 rounded">
                        {course.cpd_points} CPD
                      </span>
                    )}
                    {course.duration_hours && (
                      <span className="text-xs bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded">
                        {course.duration_hours}h
                      </span>
                    )}
                  </div>
                </div>
                {selectedCourseId === course.course_id && (
                  <CheckCircle className="h-5 w-5 text-cyan-400 flex-shrink-0" />
                )}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}


// Create Learning Lab Form Component
function CreateLearningLabForm({ token, content, setContent, generating, setGenerating }) {
  const SCENARIO_TYPES = [
    { value: 'bribery', label: 'Bribery & Corruption', description: 'Workplace bribery scenarios' },
    { value: 'data_breach', label: 'Data Breach', description: 'Data security incident response' },
    { value: 'conflict_of_interest', label: 'Conflict of Interest', description: 'COI identification and management' },
    { value: 'insider_trading', label: 'Insider Trading', description: 'Market abuse scenarios' },
    { value: 'harassment', label: 'Workplace Harassment', description: 'Harassment prevention scenarios' },
    { value: 'fraud', label: 'Fraud Detection', description: 'Financial fraud scenarios' },
    { value: 'aml', label: 'Anti-Money Laundering', description: 'AML compliance scenarios' },
    { value: 'whistleblowing', label: 'Whistleblowing', description: 'Reporting mechanisms scenarios' },
    { value: 'custom', label: 'Custom Scenario', description: 'Define your own scenario' }
  ];

  const DIFFICULTY_LEVELS = [
    { value: 'beginner', label: 'Beginner', description: 'Basic concepts' },
    { value: 'intermediate', label: 'Intermediate', description: 'Complex scenarios' },
    { value: 'advanced', label: 'Advanced', description: 'Expert-level challenges' }
  ];

  const handleCreateLab = async () => {
    if (!content.new_lab_title || !content.new_lab_scenario_type) {
      toast.error('Please fill in lab title and scenario type');
      return;
    }

    setGenerating(true);
    try {
      // Map scenario type to topic/industry
      const scenarioMapping = {
        'bribery': { topic: 'Anti-Bribery & Corruption', industry: 'General Corporate' },
        'data_breach': { topic: 'Data Privacy & Security', industry: 'Technology' },
        'conflict_of_interest': { topic: 'Conflict of Interest Management', industry: 'Financial Services' },
        'insider_trading': { topic: 'Market Abuse & Insider Trading', industry: 'Financial Services' },
        'harassment': { topic: 'Workplace Harassment Prevention', industry: 'Human Resources' },
        'fraud': { topic: 'Fraud Detection & Prevention', industry: 'Financial Services' },
        'aml': { topic: 'Anti-Money Laundering', industry: 'Banking' },
        'whistleblowing': { topic: 'Whistleblowing & Reporting', industry: 'Compliance' },
        'custom': { topic: content.new_lab_custom_context || 'Custom Compliance', industry: 'General Corporate' }
      };

      const mapping = scenarioMapping[content.new_lab_scenario_type] || scenarioMapping['custom'];

      const res = await axios.post(`${API_BASE}/api/labs/generate`, {
        title: content.new_lab_title,
        topic: mapping.topic,
        industry: mapping.industry,
        level: content.new_lab_difficulty || 'intermediate',
        outline: content.new_lab_description || `A ${content.new_lab_scenario_type} scenario focusing on ${mapping.topic}`,
        num_decision_points: content.new_lab_decision_points || 3,
        include_images: content.new_lab_include_images || false,  // Default to no images to save budget
        include_audio: false,
        target_competencies: [mapping.topic, 'Ethical Decision Making', 'Risk Assessment'],
        specific_situations: content.new_lab_custom_context ? [content.new_lab_custom_context] : []
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setContent(prev => ({
        ...prev,
        lab_id: res.data.lab_id,
        lab_title: res.data.title,
        lab_description: res.data.description,
        lab_type: content.new_lab_scenario_type,
        lab_created: true
      }));
      
      toast.success('Learning Lab created successfully!');
    } catch (error) {
      console.error('Error creating lab:', error);
      toast.error(error.response?.data?.detail || 'Failed to create Learning Lab');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-3 bg-teal-500/10 border border-teal-500/30 rounded-lg">
        <div className="flex items-center gap-2 text-teal-400 text-sm">
          <Sparkles className="h-4 w-4" />
          Create AI-Powered Learning Lab
        </div>
        <p className="text-xs text-zinc-400 mt-1">
          Generate an interactive scenario-based simulation with decision points
        </p>
      </div>

      <div>
        <Label>Lab Title *</Label>
        <Input
          value={content.new_lab_title || ''}
          onChange={(e) => setContent(prev => ({ ...prev, new_lab_title: e.target.value }))}
          placeholder="e.g., Handling a Bribery Attempt"
          className="mt-1 bg-zinc-800 border-zinc-700"
        />
      </div>

      <div>
        <Label>Scenario Type *</Label>
        <Select
          value={content.new_lab_scenario_type || ''}
          onValueChange={(v) => setContent(prev => ({ ...prev, new_lab_scenario_type: v }))}
        >
          <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
            <SelectValue placeholder="Select scenario type..." />
          </SelectTrigger>
          <SelectContent>
            {SCENARIO_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                <div className="flex flex-col">
                  <span>{type.label}</span>
                  <span className="text-xs text-zinc-500">{type.description}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {content.new_lab_scenario_type === 'custom' && (
        <div>
          <Label>Custom Scenario Context</Label>
          <Textarea
            value={content.new_lab_custom_context || ''}
            onChange={(e) => setContent(prev => ({ ...prev, new_lab_custom_context: e.target.value }))}
            placeholder="Describe the custom scenario context, roles involved, and key compliance issues..."
            rows={3}
            className="mt-1 bg-zinc-800 border-zinc-700"
          />
        </div>
      )}

      <div>
        <Label>Description (Optional)</Label>
        <Textarea
          value={content.new_lab_description || ''}
          onChange={(e) => setContent(prev => ({ ...prev, new_lab_description: e.target.value }))}
          placeholder="Brief description of the learning objectives..."
          rows={2}
          className="mt-1 bg-zinc-800 border-zinc-700"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Difficulty</Label>
          <Select
            value={content.new_lab_difficulty || 'intermediate'}
            onValueChange={(v) => setContent(prev => ({ ...prev, new_lab_difficulty: v }))}
          >
            <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DIFFICULTY_LEVELS.map((level) => (
                <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Decision Points</Label>
          <Input
            type="number"
            min={2}
            max={10}
            value={content.new_lab_decision_points || 3}
            onChange={(e) => setContent(prev => ({ ...prev, new_lab_decision_points: parseInt(e.target.value) || 3 }))}
            className="mt-1 bg-zinc-800 border-zinc-700"
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={content.new_lab_use_ai !== false}
            onChange={(e) => setContent(prev => ({ ...prev, new_lab_use_ai: e.target.checked }))}
            className="rounded border-zinc-600"
          />
          <Label className="text-sm">Generate scenario content with AI</Label>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={content.new_lab_include_images || false}
            onChange={(e) => setContent(prev => ({ ...prev, new_lab_include_images: e.target.checked }))}
            className="rounded border-zinc-600"
          />
          <Label className="text-sm">Generate step images (uses AI credits)</Label>
        </div>
      </div>

      <Button
        onClick={handleCreateLab}
        disabled={generating || !content.new_lab_title || !content.new_lab_scenario_type}
        className="w-full bg-teal-600 hover:bg-teal-700"
      >
        {generating ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Creating Learning Lab...
          </>
        ) : (
          <>
            <FlaskConical className="h-4 w-4 mr-2" />
            Create Learning Lab
          </>
        )}
      </Button>

      {content.lab_created && content.lab_id && (
        <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
          <div className="flex items-center gap-2 text-green-400 text-sm">
            <CheckCircle className="h-4 w-4" />
            Learning Lab Created Successfully!
          </div>
          <p className="text-xs text-zinc-400 mt-1">{content.lab_title}</p>
          <p className="text-xs text-zinc-500 mt-1">Lab ID: {content.lab_id}</p>
        </div>
      )}
    </div>
  );
}

// Enhanced Quiz Creation Form Component  
function CreateQuizForm({ token, content, setContent, generating, setGenerating, courseId, topicId, moduleId }) {
  const handleGenerateQuiz = async () => {
    if (!content.quiz_title && !content.quiz_context) {
      toast.error('Please enter quiz title or context');
      return;
    }

    setGenerating(true);
    try {
      const res = await axios.post(`${API_BASE}/api/course-builder/content/quiz`, {
        generation_method: 'ai',
        course_id: courseId,
        topic_id: topicId,
        module_id: moduleId,
        quiz_title: content.quiz_title || 'Module Quiz',
        quiz_context: content.quiz_context || content.quiz_title,
        num_questions: content.num_questions || 5,
        difficulty: content.quiz_difficulty || 'intermediate',
        question_types: content.question_types || ['multiple_choice', 'true_false'],
        pass_threshold: content.pass_threshold || 70,
        randomize: content.randomize || false,
        time_limit_minutes: content.time_limit_minutes || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setContent(prev => ({
        ...prev,
        quiz_id: res.data.quiz_id,
        question_count: res.data.question_count,
        quiz_created: true
      }));
      
      toast.success(`Quiz created with ${res.data.question_count} questions!`);
    } catch (error) {
      console.error('Error creating quiz:', error);
      toast.error(error.response?.data?.detail || 'Failed to create quiz');
    } finally {
      setGenerating(false);
    }
  };

  const QUESTION_TYPE_OPTIONS = [
    { value: 'multiple_choice', label: 'Multiple Choice' },
    { value: 'true_false', label: 'True/False' },
    { value: 'multi_select', label: 'Multi-Select' },
    { value: 'scenario_based', label: 'Scenario-Based' }
  ];

  return (
    <div className="space-y-4">
      <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
        <div className="flex items-center gap-2 text-yellow-400 text-sm">
          <Sparkles className="h-4 w-4" />
          AI-Powered Quiz Generator
        </div>
        <p className="text-xs text-zinc-400 mt-1">
          Generate assessment questions using Claude AI
        </p>
      </div>

      <div>
        <Label>Quiz Title</Label>
        <Input
          value={content.quiz_title || ''}
          onChange={(e) => setContent(prev => ({ ...prev, quiz_title: e.target.value }))}
          placeholder="e.g., Module Assessment"
          className="mt-1 bg-zinc-800 border-zinc-700"
        />
      </div>

      <div>
        <Label>Quiz Topic/Context *</Label>
        <Textarea
          value={content.quiz_context || ''}
          onChange={(e) => setContent(prev => ({ ...prev, quiz_context: e.target.value }))}
          placeholder="Describe what the quiz should cover, key concepts to test..."
          rows={3}
          className="mt-1 bg-zinc-800 border-zinc-700"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Number of Questions</Label>
          <Input
            type="number"
            min={3}
            max={20}
            value={content.num_questions || 5}
            onChange={(e) => setContent(prev => ({ ...prev, num_questions: parseInt(e.target.value) || 5 }))}
            className="mt-1 bg-zinc-800 border-zinc-700"
          />
        </div>
        <div>
          <Label>Pass Threshold (%)</Label>
          <Input
            type="number"
            min={50}
            max={100}
            value={content.pass_threshold || 70}
            onChange={(e) => setContent(prev => ({ ...prev, pass_threshold: parseInt(e.target.value) || 70 }))}
            className="mt-1 bg-zinc-800 border-zinc-700"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Difficulty</Label>
          <Select
            value={content.quiz_difficulty || 'intermediate'}
            onValueChange={(v) => setContent(prev => ({ ...prev, quiz_difficulty: v }))}
          >
            <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="beginner">Beginner</SelectItem>
              <SelectItem value="intermediate">Intermediate</SelectItem>
              <SelectItem value="advanced">Advanced</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Time Limit (minutes)</Label>
          <Input
            type="number"
            min={0}
            max={120}
            value={content.time_limit_minutes || 0}
            onChange={(e) => setContent(prev => ({ ...prev, time_limit_minutes: parseInt(e.target.value) || 0 }))}
            placeholder="0 = no limit"
            className="mt-1 bg-zinc-800 border-zinc-700"
          />
        </div>
      </div>

      <div>
        <Label className="mb-2 block">Question Types</Label>
        <div className="grid grid-cols-2 gap-2">
          {QUESTION_TYPE_OPTIONS.map((type) => (
            <label key={type.value} className="flex items-center gap-2 p-2 rounded border border-zinc-700 bg-zinc-800/50 cursor-pointer hover:border-zinc-600">
              <input
                type="checkbox"
                checked={(content.question_types || ['multiple_choice', 'true_false']).includes(type.value)}
                onChange={(e) => {
                  const current = content.question_types || ['multiple_choice', 'true_false'];
                  if (e.target.checked) {
                    setContent(prev => ({ ...prev, question_types: [...current, type.value] }));
                  } else {
                    setContent(prev => ({ ...prev, question_types: current.filter(t => t !== type.value) }));
                  }
                }}
                className="rounded border-zinc-600"
              />
              <span className="text-sm text-zinc-300">{type.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={content.randomize || false}
            onChange={(e) => setContent(prev => ({ ...prev, randomize: e.target.checked }))}
            className="rounded border-zinc-600"
          />
          <span className="text-sm text-zinc-300">Randomize questions</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={content.show_correct_answers || true}
            onChange={(e) => setContent(prev => ({ ...prev, show_correct_answers: e.target.checked }))}
            className="rounded border-zinc-600"
          />
          <span className="text-sm text-zinc-300">Show correct answers</span>
        </label>
      </div>

      <Button
        onClick={handleGenerateQuiz}
        disabled={generating || !content.quiz_context}
        className="w-full bg-yellow-600 hover:bg-yellow-700"
      >
        {generating ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Generating Quiz...
          </>
        ) : (
          <>
            <Brain className="h-4 w-4 mr-2" />
            Generate AI Quiz
          </>
        )}
      </Button>

      {content.quiz_created && content.quiz_id && (
        <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
          <div className="flex items-center gap-2 text-green-400 text-sm">
            <CheckCircle className="h-4 w-4" />
            Quiz Created: {content.question_count} questions
          </div>
          <p className="text-xs text-zinc-400 mt-1">Quiz ID: {content.quiz_id}</p>
        </div>
      )}
    </div>
  );
}


// Quiz Bank Selector Component
function QuizBankSelector({ token, selectedQuizId, onSelect }) {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchQuizzes();
  }, []);

  const fetchQuizzes = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/quizzes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuizzes(res.data.quizzes || res.data || []);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
      // If endpoint doesn't exist, show empty state
      setQuizzes([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredQuizzes = quizzes.filter(quiz => 
    quiz.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    quiz.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-yellow-400" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Input
        placeholder="Search quizzes..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="bg-zinc-800 border-zinc-700"
      />
      
      <div className="max-h-60 overflow-y-auto space-y-2">
        {filteredQuizzes.length === 0 ? (
          <div className="text-center py-6 border border-dashed border-zinc-700 rounded-lg">
            <HelpCircle className="h-8 w-8 text-zinc-500 mx-auto mb-2" />
            <p className="text-sm text-zinc-400">No quizzes in quiz bank</p>
            <p className="text-xs text-zinc-500 mt-1">Create a new quiz using AI generation</p>
          </div>
        ) : (
          filteredQuizzes.map((quiz) => (
            <button
              key={quiz.quiz_id}
              onClick={() => onSelect(quiz)}
              className={`w-full p-3 rounded-lg border text-left transition-all ${
                selectedQuizId === quiz.quiz_id
                  ? 'border-yellow-500 bg-yellow-500/10'
                  : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
              }`}
            >
              <div className="flex items-start gap-3">
                <HelpCircle className={`h-5 w-5 mt-0.5 ${
                  selectedQuizId === quiz.quiz_id ? 'text-yellow-400' : 'text-zinc-500'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{quiz.title}</p>
                  <p className="text-xs text-zinc-400 line-clamp-2 mt-1">{quiz.description}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded">
                      {quiz.question_count || quiz.questions?.length || 0} questions
                    </span>
                    {quiz.pass_threshold && (
                      <span className="text-xs bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded">
                        Pass: {quiz.pass_threshold}%
                      </span>
                    )}
                  </div>
                </div>
                {selectedQuizId === quiz.quiz_id && (
                  <CheckCircle className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                )}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
